import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"
import { Block, Spacer } from "../../design-system/atoms/layout-primitives"
import ContentBlock from "../../design-system/molecules/content-block"
import {
  ITestimonialCard,
  TESTIMONIAL_TYPENAME,
} from "../../design-system/molecules/testimonial-card"
import TestimonialCardSlider from "../../design-system/organisms/testimonial-card-slider"
import { IFluidImageQuery } from "../../design-system/types/imageQuery.types"
import withAnimation, { FadeIn } from "../../design-system/util/withAnimation"
import Grid from "../shared/grid"
const Span = styled.span`
  display: inline-block;
`
const AnimatedSpan = withAnimation(Span)

interface ITestimonialsStaticQuery {
  simonLogo: IFluidImageQuery
  mohawkLogo: IFluidImageQuery
  miracleLogo: IFluidImageQuery
  sonaeLogo: IFluidImageQuery
  mallPlazaLogo: IFluidImageQuery
}

const ApplicationTestimonials = () => {
  const { simonLogo, mohawkLogo, miracleLogo, sonaeLogo, mallPlazaLogo } =
    useStaticQuery<ITestimonialsStaticQuery>(graphql`
      query {
        simonLogo: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/logo selector/simon.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        mohawkLogo: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/logo selector/mohawk college.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        miracleLogo: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/logo selector/miracle mile shops.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        sonaeLogo: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/logo selector/sonae sierra.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        mallPlazaLogo: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/logo selector/mallplaza.png"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `)

  const testimonialDetails: ITestimonialCard[] = [
    {
      // Simon
      logos: [simonLogo.childImageSharp],
      author: "Mikael Thygesen",
      jobTitle: "Chief Marketing Officer",
      quote:
        "“Simon is continually looking for opportunities to enhance the shopping experience and we quickly recognized the potential of this new technology to do just that. Consumer response and feedback to our new interactive maps has exceeded expectations, with usage levels well above the targets we set.”",
    },
    {
      // Sonae Sierra
      logos: [sonaeLogo.childImageSharp],
      author: "Ana Fé",
      jobTitle: "Digital Marketing Coordinator",
      quote:
        "“We’re continually looking for state of the art technologies that can increase shopper engagement. Mappedin’s web wayfinding platform adds a consistent means for shoppers to navigate our Shopping Centers, providing a completely interactive and immersive experience. We’re very glad to take advantage of the Mappedin technology that has highly contributed to improving our customers digital experience.”",
    },
    {
      // Mohawk College
      logos: [mohawkLogo.childImageSharp],
      author: "Samara Young",
      jobTitle: "Manager, Student Lifecycle Communications",
      quote:
        "“Mohawk College was looking for an interactive digital mapping solution to feature on our website so that students can search for classrooms, services, and more at two of our campuses. Mappedin’s solution provided this, along with a powerful Map Editor platform that allows us to easily update our maps as locations change and grow.”",
    },
    {
      // Mallplaza
      logos: [mallPlazaLogo.childImageSharp],
      author: "Violena Meersohn",
      jobTitle: "Agility and Digital Products Deputy Manager",
      quote:
        "“Mappedin’s web solution offers a flexible and interactive mapping solution for visitors to our shopping centres and the Map Editor makes it easy to manage all of the maps in the Mallplaza portfolio. We really value the customer experience that Mappedin delivered to our team from start to finish. We’re looking forward to continuing our work together.”",
    },
    {
      // Miracle Mile
      logos: [miracleLogo.childImageSharp],
      author: "Wendy Albert",
      jobTitle: "Senior Director of Marketing",
      quote:
        "“Mappedin’s responsive mapping solution with blue dot wayfinding allows us to provide interactive and intuitive wayfinding to visitors of Miracle Mile Shops. By integrating the maps with a conversational AI platform, it is simple for tourists and other guests to search, discover, and navigate our venue directly from our website.”",
    },
  ].map((item, index) => ({
    ...item,
    __typename: TESTIMONIAL_TYPENAME,
    contentful_id: `${index}`,
  }))
  return (
    <>
      <Grid>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
        <Block
          col={{ sm: "1/-1", md: "2/-2", lg: "3/-3" }}
          variants={[
            css`
              text-align: center;
            `,
          ]}
        >
          <ContentBlock.H3
            title={
              <AnimatedSpan animation={FadeIn}>
                Industry-Leading Service
              </AnimatedSpan>
            }
            description={
              <AnimatedSpan animation={FadeIn}>
                We put our customers and partners first. This emphasis on client
                success has led us to digitize more indoor maps than any other
                provider. We are committed to continually delivering value and
                exceeding expectations.
              </AnimatedSpan>
            }
            bodyVariants={css`
              font-weight: 300;
            `}
          />
        </Block>
        <Spacer height={{ sm: "48px", lg: "64px" }} />
      </Grid>
      {/* Testimonial slider has its own grid wrapper */}
      <TestimonialCardSlider testimonials={testimonialDetails} />
      <Grid>
        <Spacer height={{ sm: "48px", md: "48px", lg: "96px" }} />
      </Grid>
    </>
  )
}

export default ApplicationTestimonials
